import { useRef, useState } from "react";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Popover from "@mui/material/Popover";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  ErrorText,
  FieldLabel,
  FieldLabelContainer,
  FieldWrapper,
  FormDropdownChevronIcon,
  MultiSelectCommaValue,
  MultiSelectFormControl,
  MultiSelectInlinePlaceholder,
} from "styles/formStyleComponents";
import InfoIconWithTooltip from "./InfoIconWithTooltip";
import {
  CheckboxSelectBox,
  CheckboxSelectCheckIcon,
  CheckboxSelectChevron,
  CheckboxSelectErrorText,
  CheckboxSelectItemLabel,
  CheckboxSelectMenuHeader,
  CheckboxSelectMenuItem,
  CheckboxSelectTrigger,
  CheckboxSelectTriggerLabel,
  checkboxSelectPopoverPaperSx,
} from "./MultiSelectField.styles";

const getOptionValue = (option) => {
  if (option == null) {
    return "";
  }
  if (typeof option === "object" && option.value !== undefined) {
    return option.value;
  }
  return option;
};

const getOptionLabel = (option) => {
  if (option == null) {
    return "";
  }
  if (typeof option === "object") {
    return String(option.label ?? option.value ?? "");
  }
  return String(option);
};

const resolveSelectedLabel = (val, options, roleLabelMap = {}) => {
  if (val == null || val === "") {
    return "";
  }
  if (typeof val === "object") {
    return String(val.label ?? val.value ?? "");
  }
  const match = options.find(
    (opt) =>
      opt === val ||
      String(getOptionValue(opt)) === String(val) ||
      String(opt?.label) === String(val)
  );
  return String(
    match?.label ?? match?.value ?? roleLabelMap[val] ?? val
  );
};

const normalizeSelectedValues = (selected, options) => {
  const list = Array.isArray(selected)
    ? selected
    : selected != null
      ? [selected]
      : [];
  return list
    .map((item) => {
      if (item == null || item === "") {
        return null;
      }
      if (typeof item === "object") {
        if (item.value !== undefined) {
          return item.value;
        }
        if (item.label !== undefined) {
          const byLabel = options.find(
            (opt) => String(opt?.label) === String(item.label)
          );
          return byLabel != null ? getOptionValue(byLabel) : item.label;
        }
      }
      const match = options.find(
        (opt) =>
          getOptionValue(opt) === item ||
          String(getOptionValue(opt)) === String(item) ||
          String(opt?.label) === String(item)
      );
      return match != null ? getOptionValue(match) : item;
    })
    .filter((value) => value != null && value !== "");
};

const formatCommaSeparatedSelection = (selected, options, roleLabelMap) =>
  (selected ?? [])
    .map((val) => resolveSelectedLabel(val, options, roleLabelMap))
    .filter(Boolean)
    .join(", ");

const selectedValueSet = (selectedValues) =>
  new Set(selectedValues.map((value) => String(value)));

const CheckboxMenuMultiSelect = ({
  name,
  label,
  fieldLabel,
  selectedValues,
  options,
  disabled,
  error,
  emptyLabel,
  menuTitle,
  roleLabelMap,
  stopClickPropagation,
  ariaLabel,
  onChange,
  onOpen,
  onClose,
}) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const displayLabel = formatCommaSeparatedSelection(
    selectedValues,
    options,
    roleLabelMap
  );
  const triggerWidth = Math.max(
    166,
    Math.round(anchorRef.current?.getBoundingClientRect?.().width || 166)
  );
  const canOpen = !disabled && options.length > 0;
  const noun = String(label || fieldLabel || "Roles").replace(/ \*$/, "");
  const triggerAriaLabel =
    ariaLabel ||
    (displayLabel
      ? `${noun}: ${displayLabel}. Change ${noun.toLowerCase()}`
      : `No ${noun.toLowerCase()}. Add ${noun.toLowerCase()}`);

  const closeMenu = () => {
    setOpen(false);
    onClose?.();
  };

  const handleTriggerClick = () => {
    if (!canOpen) return;
    if (open) {
      closeMenu();
      return;
    }
    onOpen?.();
    setOpen(true);
  };

  const handleToggle = (option) => {
    if (disabled || option?.locked) return;
    const optionValue = getOptionValue(option);
    const selected = selectedValueSet(selectedValues);
    const key = String(optionValue);
    const next = selected.has(key)
      ? selectedValues.filter((value) => String(value) !== key)
      : [...selectedValues, optionValue];
    onChange(name, next);
  };

  const wrapperProps = stopClickPropagation
    ? {
        onClick: (event) => event.stopPropagation(),
        onKeyDown: (event) => {
          if (!open) return;
          if (event.key === "Enter") {
            event.preventDefault();
            closeMenu();
          }
        },
      }
    : {
        onKeyDown: (event) => {
          if (!open) return;
          if (event.key === "Enter") {
            event.preventDefault();
            closeMenu();
          }
        },
      };

  return (
    <div {...wrapperProps}>
      <CheckboxSelectTrigger
        ref={anchorRef}
        type="button"
        $open={open}
        $error={!!error}
        disabled={!canOpen}
        aria-haspopup="true"
        aria-expanded={open ? "true" : "false"}
        aria-invalid={!!error}
        aria-label={triggerAriaLabel}
        onClick={handleTriggerClick}
      >
        <CheckboxSelectTriggerLabel $empty={!displayLabel} $error={!!error && !displayLabel}>
          {displayLabel || emptyLabel || fieldLabel}
        </CheckboxSelectTriggerLabel>
        <CheckboxSelectChevron $open={open} aria-hidden>
          <KeyboardArrowDownIcon fontSize="inherit" />
        </CheckboxSelectChevron>
      </CheckboxSelectTrigger>
      {error ? <CheckboxSelectErrorText>{error}</CheckboxSelectErrorText> : null}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            "data-menu": "1",
            sx: checkboxSelectPopoverPaperSx(triggerWidth),
          },
        }}
      >
        <CheckboxSelectMenuHeader>
          {menuTitle || noun}
        </CheckboxSelectMenuHeader>
        <div role="group" aria-label={ariaLabel || menuTitle || noun}>
          {options.map((option) => {
            const optionValue = getOptionValue(option);
            const checked = selectedValueSet(selectedValues).has(
              String(optionValue)
            );
            return (
              <CheckboxSelectMenuItem
                key={String(optionValue)}
                type="button"
                role="checkbox"
                aria-checked={checked}
                aria-label={getOptionLabel(option)}
                disabled={Boolean(option?.locked) || disabled}
                onClick={() => handleToggle(option)}
              >
                <CheckboxSelectBox $checked={checked}>
                  {checked ? <CheckboxSelectCheckIcon /> : null}
                </CheckboxSelectBox>
                <CheckboxSelectItemLabel>
                  {getOptionLabel(option)}
                </CheckboxSelectItemLabel>
              </CheckboxSelectMenuItem>
            );
          })}
        </div>
      </Popover>
    </div>
  );
};

const MultiSelectFieldComponent = ({
  name,
  label,
  value,
  required,
  disabled,
  error,
  info,
  options = [],
  onChange,
  onOpen,
  onClose,
  compact = false,
  inline = false,
  valueDisplay = "chips",
  emptyLabel,
  menuTitle,
  roleLabelMap = {},
  stopClickPropagation = false,
  ariaLabel,
}) => {
  const fieldLabel = required ? `${label} *` : label;
  const hideFloatingLabel = compact && inline;
  const useCommaDisplay = valueDisplay === "comma";
  const selectedValues = normalizeSelectedValues(value, options);
  const hasSelection = selectedValues.length > 0;
  const useFloatingLabel = !hideFloatingLabel && hasSelection;
  const showInlinePlaceholder = hideFloatingLabel || !hasSelection;
  const labelId = `${name}-select-label`;

  const emptyPlaceholder = (
    <MultiSelectInlinePlaceholder hasError={!!error}>
      {fieldLabel}
    </MultiSelectInlinePlaceholder>
  );

  return (
    <FieldWrapper compact={compact} inline={inline}>
      {info && (
        <FieldLabelContainer>
          <FieldLabel>{fieldLabel}</FieldLabel>
          <InfoIconWithTooltip info={info} />
        </FieldLabelContainer>
      )}
      {hideFloatingLabel ? (
        <CheckboxMenuMultiSelect
          name={name}
          label={label}
          fieldLabel={fieldLabel}
          selectedValues={selectedValues}
          options={options}
          disabled={disabled}
          error={error}
          emptyLabel={emptyLabel}
          menuTitle={menuTitle}
          roleLabelMap={roleLabelMap}
          stopClickPropagation={stopClickPropagation}
          ariaLabel={ariaLabel}
          onChange={onChange}
          onOpen={onOpen}
          onClose={onClose}
        />
      ) : (
        <MultiSelectFormControl
          fullWidth
          size="small"
          error={!!error}
          compactInline={false}
          hideLegend={!hasSelection}
        >
          {useFloatingLabel ? (
            <InputLabel id={labelId} shrink>
              {fieldLabel}
            </InputLabel>
          ) : null}
          <Select
            multiple
            displayEmpty
            error={!!error}
            IconComponent={FormDropdownChevronIcon}
            labelId={useFloatingLabel ? labelId : undefined}
            label={useFloatingLabel ? fieldLabel : undefined}
            value={selectedValues}
            onChange={(e) => onChange(name, e.target.value)}
            disabled={disabled}
            onOpen={onOpen}
            onClose={onClose}
            input={
              <OutlinedInput
                error={!!error}
                notched={useFloatingLabel}
                label={useFloatingLabel ? fieldLabel : undefined}
                inputProps={{
                  autoComplete: "off",
                  "aria-label": fieldLabel,
                }}
              />
            }
            renderValue={(selected) => {
              if (!selected?.length) {
                if (showInlinePlaceholder) {
                  return emptyPlaceholder;
                }
                return null;
              }
              if (useCommaDisplay) {
                return (
                  <MultiSelectCommaValue component="span">
                    {formatCommaSeparatedSelection(selected, options)}
                  </MultiSelectCommaValue>
                );
              }
              return formatCommaSeparatedSelection(selected, options);
            }}
          >
            {options.map((option) => {
              const optionValue = getOptionValue(option);
              return (
                <MenuItem key={String(optionValue)} value={optionValue}>
                  {option?.label || option}
                </MenuItem>
              );
            })}
          </Select>
          {error ? <ErrorText error>{error}</ErrorText> : null}
        </MultiSelectFormControl>
      )}
    </FieldWrapper>
  );
};

export default MultiSelectFieldComponent;
